/* eslint-disable react/style-prop-object */
import React from "react";
import { Attention, Button, DialogNew } from "@optimizely/axiom";

type ConfirmationDialogProps = {
    additionalActions?: React.ReactNode;
    children?: string | React.ReactNode | React.ReactNode[];
    callToActionText?: string;
    error?: string | undefined | null;
    /**
     * Blocks confirmation without blocking Cancel. Use for dialogs whose body is still resolving
     * what the user is about to confirm; `loading` disables both buttons and is for the save itself.
     */
    isConfirmDisabled?: boolean;
    loading?: boolean;
    onCancel: () => void;
    onConfirmation: () => void;
    style?:
        | "underline"
        | "danger"
        | "danger-outline"
        | "highlight"
        | "outline"
        | "outline-reverse"
        | "plain"
        | "toggle"
        | "unstyled"
        | undefined;
    title?: string;
    subtitle?: string | React.ReactNode | React.ReactNode[];
};

export const ConfirmationDialog = ({
    additionalActions,
    children,
    callToActionText = "Confirm",
    error,
    isConfirmDisabled = false,
    loading,
    onCancel,
    onConfirmation,
    style = "danger",
    title = "Confirm",
    subtitle
}: ConfirmationDialogProps) => {
    // DialogNew renders `subtitle` as a list, so every entry needs a stable key. Wrap/clone each node
    // with one to avoid React "unique key" warnings (e.g. when subtitle is a fragment).
    let subtitleContent: React.ReactNode[] = (Array.isArray(subtitle) ? subtitle : [subtitle])
        .filter((e) => !!e)
        .map((node, index) =>
            React.isValidElement(node) ? (
                React.cloneElement(node, { key: node.key ?? `subtitle-${index}` })
            ) : (
                <React.Fragment key={`subtitle-${index}`}>{node}</React.Fragment>
            )
        );

    if (error) {
        subtitleContent = [
            ...subtitleContent,
            <Attention type="bad-news" key="subtitle-error">
                {error}
            </Attention>
        ];
    }
    return (
        <DialogNew
            footerButtonList={[
                <div key={2} style={{ display: "inline-flex", position: "absolute", left: "16px" }}>
                    {additionalActions && additionalActions}
                </div>,
                <Button
                    data-cy="confirmation-dialog-cancel"
                    key={0}
                    isDisabled={loading}
                    style="plain"
                    onClick={onCancel}
                >
                    Cancel
                </Button>,
                <Button
                    key={1}
                    data-cy="confirmation-dialog-cta"
                    isDisabled={loading || isConfirmDisabled}
                    isLoading={loading}
                    loadingText={callToActionText}
                    style={style}
                    onClick={onConfirmation}
                >
                    {callToActionText}
                </Button>
            ]}
            subtitle={subtitleContent}
            title={title}
            hasCloseButton
            hasOverlay
            onClose={onCancel}
        >
            <div className="push--bottom">{children}</div>
        </DialogNew>
    );
};

ConfirmationDialog.displayName = "ConfirmationDialog";
